import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input, Label, FormField, FormError } from '@redpanda/ui';
import api from '../services/api';

interface CapacityFormData {
  dailyCapacity: number | null;
  monthlyCapacity: number | null;
}

interface CapacitySettingsEditorProps {
  storeId: string;
  initialDailyCapacity?: number | null;
  initialMonthlyCapacity?: number | null;
}

export interface CapacitySettingsEditorRef {
  save: () => Promise<void>;
}

const CapacitySettingsEditor = forwardRef<CapacitySettingsEditorRef, CapacitySettingsEditorProps>(({
  storeId,
  initialDailyCapacity,
  initialMonthlyCapacity,
}, ref) => {
  const { t } = useTranslation(['components', 'translation']);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CapacityFormData>({
    defaultValues: {
      dailyCapacity: initialDailyCapacity ?? null,
      monthlyCapacity: initialMonthlyCapacity ?? null,
    },
  });

  const [enableDailyLimit, setEnableDailyLimit] = useState(!!initialDailyCapacity);
  const [enableMonthlyLimit, setEnableMonthlyLimit] = useState(!!initialMonthlyCapacity);

  useImperativeHandle(ref, () => ({
    save: () => {
      return new Promise<void>((resolve, reject) => {
        handleSubmit(async (data) => {
          await api.patch(`/stores/${storeId}`, {
            dailyCapacity: enableDailyLimit ? data.dailyCapacity : null,
            monthlyCapacity: enableMonthlyLimit ? data.monthlyCapacity : null,
          });
          resolve();
        }, () => {
          reject(new Error('Capacity validation failed'));
        })();
      });
    },
  }));

  // 同步初始值
  useEffect(() => {
    reset({
      dailyCapacity: initialDailyCapacity ?? null,
      monthlyCapacity: initialMonthlyCapacity ?? null,
    });
    setEnableDailyLimit(!!initialDailyCapacity);
    setEnableMonthlyLimit(!!initialMonthlyCapacity);
  }, [initialDailyCapacity, initialMonthlyCapacity, reset]);

  return (
    <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        {t('components:capacitySettings.title')}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('components:capacitySettings.subtitle')}
      </p>

      <div className="space-y-6">
        {/* 每日容量限制 */}
        <div>
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="enableDailyLimit"
              checked={enableDailyLimit}
              onChange={(e) => setEnableDailyLimit(e.target.checked)}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 rounded"
            />
            <label
              htmlFor="enableDailyLimit"
              className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('components:capacitySettings.enableDailyLimit')}
            </label>
          </div>
          {enableDailyLimit && (
            <FormField>
              <Label htmlFor="dailyCapacity">{t('components:capacitySettings.dailyCapacity')}</Label>
              <Input
                type="number"
                id="dailyCapacity"
                {...register('dailyCapacity', {
                  required: enableDailyLimit ? t('components:capacitySettings.dailyCapacityRequired') : false,
                  min: { value: 1, message: t('components:capacitySettings.minValue') },
                })}
                placeholder={t('components:capacitySettings.dailyCapacityPlaceholder')}
                variant={errors.dailyCapacity ? 'error' : 'default'}
                disabled={!enableDailyLimit}
              />
              {errors.dailyCapacity && (
                <FormError>{errors.dailyCapacity.message}</FormError>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('components:capacitySettings.dailyCapacityHelpText')}
              </p>
            </FormField>
          )}
        </div>

        {/* 每月容量限制 */}
        <div>
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              id="enableMonthlyLimit"
              checked={enableMonthlyLimit}
              onChange={(e) => setEnableMonthlyLimit(e.target.checked)}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-slate-300 rounded"
            />
            <label
              htmlFor="enableMonthlyLimit"
              className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('components:capacitySettings.enableMonthlyLimit')}
            </label>
          </div>
          {enableMonthlyLimit && (
            <FormField>
              <Label htmlFor="monthlyCapacity">{t('components:capacitySettings.monthlyCapacity')}</Label>
              <Input
                type="number"
                id="monthlyCapacity"
                {...register('monthlyCapacity', {
                  required: enableMonthlyLimit ? t('components:capacitySettings.monthlyCapacityRequired') : false,
                  min: { value: 1, message: t('components:capacitySettings.minValue') },
                })}
                placeholder={t('components:capacitySettings.monthlyCapacityPlaceholder')}
                variant={errors.monthlyCapacity ? 'error' : 'default'}
                disabled={!enableMonthlyLimit}
              />
              {errors.monthlyCapacity && (
                <FormError>{errors.monthlyCapacity.message}</FormError>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('components:capacitySettings.monthlyCapacityHelpText')}
              </p>
            </FormField>
          )}
        </div>
      </div>
    </div>
  );
});

CapacitySettingsEditor.displayName = 'CapacitySettingsEditor';

export default CapacitySettingsEditor;
